import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, MenuItem, FormControl, Select, InputBase } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useLanguage } from './../../../context/LanguageContext/LanguageContext';
import Flag from 'react-world-flags'; // Import the flag library
import { useTheme } from '@mui/material/styles';

const LanguageDropdown = ({ styleName = 'dropdown' }) => {
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const theme = useTheme();
  const buttonRef = useRef(null); 
  let timer;

  useEffect(() => {
    const isRTL = language === 'Arabic';
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  }, [language]);

  const [anchorEl, setAnchorEl] = useState(null);

  // Handle hover for the first dropdown style
  const handleLanguageHover = (event) => {
    clearTimeout(timer);
    setAnchorEl(event.currentTarget);
    setSubmenuOpen(true);
  };

  const handleLanguageClose = (selectedLang) => {
    toggleLanguage();
    setSubmenuOpen(false);
    setAnchorEl(null);
    window.location.reload();
  };

  const handleMouseLeave = () => {
    // Delay the closing of the menu to allow the user to hover over it
    timer = setTimeout(() => {
      setSubmenuOpen(false);
      setAnchorEl(null);
    }, 200); 
  };

  return (
    <>
      {styleName === 'dropdown' ? (
        // Dropdown Style using Box instead of Menu
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Button
            ref={buttonRef}
            variant="text"
            color="primary"
            onMouseEnter={handleLanguageHover} // Trigger dropdown on hover
            onMouseLeave={handleMouseLeave} // Close dropdown on hover leave
            sx={{ textTransform: 'none', color: '#A1A1A1' }}
            endIcon={submenuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          >
            {language}
          </Button>

          {submenuOpen && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%', // Align below the button
                left: '50%', // Center horizontally based on button position
                transform: 'translateX(-50%)', // Adjust to precisely center
                backgroundColor: '#fff',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '4px',
                zIndex: 1300,
                minWidth: '100px',
                width: 'auto',
                display: 'flex',
                flexDirection: 'column',
                marginTop: '9px',
                transition: 'transform 0.3s ease',
                justifyContent: 'center', // Center vertically
                alignItems: 'center', // Center horizontally
              }}
              onMouseEnter={handleLanguageHover}
              onMouseLeave={handleMouseLeave}
            >
              <MenuItem
                onClick={() => handleLanguageClose('English')}
                sx={{
                  padding: '8px 16px',
                  color: 'var(--text-color)',
                  textDecoration: 'none',
                  width: '100%', // Ensure full width for item alignment
                  display: 'flex',
                  justifyContent: 'center', // Center content horizontally
                  '&:hover': {
                    color: 'var(--secondary-color)',
                    backgroundColor: 'var(--primary-color)',
                  },
                  '&.active': {
                    color: 'var(--secondary-color)',
                    backgroundColor: 'var(--primary-color)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap="4px">
                  <Flag code="us" style={{ width: '20px', height: '15px' }} />
                  <span style={{ fontWeight: 'bold' }}>EN</span>
                </Box>
              </MenuItem>

              <MenuItem
                onClick={() => handleLanguageClose('Arabic')}
                sx={{
                  padding: '8px 16px',
                  color: 'var(--text-color)',
                  textDecoration: 'none',
                  width: '100%', // Ensure full width for item alignment
                  display: 'flex',
                  justifyContent: 'center', // Center content horizontally
                  '&:hover': {
                    color: 'var(--secondary-color)',
                    backgroundColor: 'var(--primary-color)',
                  },
                  '&.active': {
                    color: 'var(--secondary-color)',
                    backgroundColor: 'var(--primary-color)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap="4px">
                  <Flag code="SA" style={{ width: '20px', height: '15px' }} />
                  <span style={{ fontWeight: 'bold' }}>AR</span>
                </Box>
              </MenuItem>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControl>
            <Select
              value={language}
              onChange={(event) => handleLanguageClose(event.target.value)}
              displayEmpty
              input={<InputBase />}
              sx={{
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '20px',
                backgroundColor: theme.palette.background.paper,
                border: 'none',
                borderRadius: '5px',
                position: 'relative',
                '.MuiSelect-icon': {
                  color: '#174689',
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)', // Add hover effect
                },
              }}
            >
              <MenuItem value="English">
                <Box display="flex" alignItems="center" gap="4px">
                  <Flag code="us" style={{ width: '20px', height: '15px' }} />
                  <span style={{ color: '#174689', fontWeight: 'bold' }}>EN</span>
                </Box>
              </MenuItem>
              <MenuItem value="Arabic">
                <Box display="flex" alignItems="center" gap="4px">
                  <Flag code="SA" style={{ width: '20px', height: '15px' }} />
                  <span style={{ color: '#174689', fontWeight: 'bold' }}>AR</span>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
    </>
  );
};

export default LanguageDropdown;
